import { App, Checkbox, Input } from "antd";
import ActionButton from "../../../../masterComponents/actionButton";
import Icon from "../../../../masterComponents/Icon";
import { PHRASES_DICTIONARY } from "../../../../../entities/phrasesDictionary";
import { StyledAgentTableToolbar } from "../../analysisAgent.style";

import {
  CheckOutlined,
  ClearOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import { useEffect, useState } from "react";
import type {
  AgentTableToolbarProps,
  BuildActionsType,
  ToolbarActionRow,
} from "./types/AgentTableToolbarType";

function stripTagFromActionRows<
  T extends { tag?: string; actionSource?: string },
>(rows: T[], action_type: string) {
  return rows.map(({ tag: _t, actionSource: _s, ...rest }) => ({
    ...rest,
    action_type,
  }));
}

const AgentTableToolbar: React.FC<AgentTableToolbarProps> = ({
  mode,
  parentState,
  rows,
  disabled,
  pendingBtn,
  setPendingBtn,
  onSubmit,
  onValidate,
  isLoadingSubmit,
  isLoadingValidate,
  showOverride,
  warningCount,
}) => {
  const { modal } = App.useApp();

  const [isCheckboxEnabled, setIsCheckboxEnabled] = useState(false);
  const [overrideValue, setOverrideValue] = useState("");

  useEffect(() => {
    if (!isLoadingSubmit) {
      setOverrideValue("");
      setIsCheckboxEnabled(false);
    }
  }, [isLoadingSubmit]);

  const hasSelection = Boolean(rows?.length);

  const verifyTagsOk =
    mode === "AgentTable" && hasSelection && rows.every((r) => r.tag === "llm");

  const finalsTagsOk =
    mode === "AgentTable" &&
    hasSelection &&
    rows.every((r) => r.tag === "finals");

  const validatedTagsOk =
    mode === "AgentTable" &&
    hasSelection &&
    rows.every((r) => r.tag === "validated");

  const verifyEnabled =
    mode === "SourceValuesTable"
      ? hasSelection && parentState === "llm"
      : verifyTagsOk;

  const finalsEnabled =
    mode === "SourceValuesTable"
      ? hasSelection && parentState === "finals"
      : finalsTagsOk;

  const validatedEnabled =
    mode === "SourceValuesTable"
      ? hasSelection && parentState === "validated"
      : validatedTagsOk;

  const showVerifyButton = mode === "AgentTable" || parentState === "llm";
  const showClearVerifyButton =
    mode === "AgentTable" || parentState === "finals";
  const showValidateButton = mode === "AgentTable" || parentState === "finals";
  const showClearValidationButton =
    mode === "AgentTable" || parentState === "validated";

  const showOverrideUi =
    showOverride ??
    (mode === "SourceValuesTable" ? parentState === "llm" : true);

  const countForModal =
    warningCount ??
    rows
      .filter((el) => el.tag === "validated" || el.tag === "finals")
      .reduce((sum, el) => sum + (Number(el.frequency) || 0), 0);

  const buildActions: BuildActionsType = (action_type, withOverride = false) => {
    const trimmed = overrideValue.trim();
    const shouldOverride = withOverride && isCheckboxEnabled;

    const payloadRows: ToolbarActionRow[] = shouldOverride
      ? rows.map((row) => ({ ...row, target_value: trimmed }))
      : rows;

    return stripTagFromActionRows(payloadRows, action_type);
  };

  return (
    <StyledAgentTableToolbar className="agent-table-toolbar">
      {showOverrideUi && (
        <>
          <Checkbox
            title="Override value"
            checked={isCheckboxEnabled}
            disabled={!verifyEnabled || disabled}
            onChange={(e) => setIsCheckboxEnabled(e.target.checked)}
          />
          <Input
            className="agent-table-toolbar__input"
            style={{ width: 300 }}
            placeholder={
              !verifyEnabled
                ? "Only for verification"
                : isCheckboxEnabled
                  ? "Enter override value..."
                  : "Select checkbox for use override value"
            }
            disabled={!isCheckboxEnabled || !verifyEnabled || disabled}
            value={overrideValue}
            onChange={(e) => setOverrideValue(e.target.value)}
          />
        </>
      )}
      {showVerifyButton && (
        <ActionButton
          text="Verify"
          className="primary-btn-color"
          icon={<Icon icon={CheckOutlined} />}
          iconPosition="end"
          disabled={!verifyEnabled || disabled || isLoadingSubmit}
          loading={isLoadingSubmit && pendingBtn === "verify"}
          onClick={() => {
            setPendingBtn("verify");
            onSubmit(buildActions("SUBMIT", true));
          }}
        />
      )}
      {showClearVerifyButton && (
        <ActionButton
          text="Clear Verification"
          className="primary-btn-color"
          icon={<Icon icon={ClearOutlined} />}
          iconPosition="end"
          disabled={!finalsEnabled || disabled || isLoadingValidate}
          loading={isLoadingSubmit && pendingBtn === "clearVerify"}
          onClick={() => {
            setPendingBtn("clearVerify");
            onSubmit(buildActions("CLEAR"));
          }}
        />
      )}
      {showValidateButton && (
        <ActionButton
          text="Validate"
          className="primary-btn-color"
          icon={<Icon icon={CheckCircleOutlined} />}
          iconPosition="end"
          disabled={!finalsEnabled || disabled || isLoadingSubmit}
          loading={isLoadingValidate && pendingBtn === "validation"}
          onClick={() => {
            modal.warning({
              title: "Warning!",
              content: (
                <div style={{ whiteSpace: "pre-line" }}>
                  {PHRASES_DICTIONARY.WARNINGS.warning_agent_validation(
                    countForModal,
                  )}
                </div>
              ),
              onOk: () => {
                setPendingBtn("validation");
                onValidate(buildActions("VALIDATE"));
              },
              okCancel: true,
              transitionName: "ant-fade",
            });
          }}
        />
      )}
      {showClearValidationButton && (
        <ActionButton
          text="Clear Validation"
          className="primary-btn-color"
          icon={<Icon icon={ClearOutlined} />}
          iconPosition="end"
          disabled={!validatedEnabled || disabled}
          loading={isLoadingValidate && pendingBtn === "clearValidation"}
          onClick={() => {
            modal.warning({
              title: "Warning!",
              content: (
                <div style={{ whiteSpace: "pre-line" }}>
                  {PHRASES_DICTIONARY.WARNINGS.warning_agent_clear_validation(
                    countForModal,
                  )}
                </div>
              ),
              onOk: () => {
                setPendingBtn("clearValidation");
                onValidate(buildActions("CLEAR_VALIDATION"));
              },
              okCancel: true,
              transitionName: "ant-fade",
            });
          }}
        />
      )}
    </StyledAgentTableToolbar>
  );
};

export default AgentTableToolbar;
